import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  fetchEntityArticles,
  fetchEntityBySlug,
  fetchRelatedEntities,
} from '../services/graphApi';
import { WINDOW_OPTIONS, windowLabel, windowRange } from '../utils/window';
import { pluralize } from '../utils/pluralize';
import { usePageMeta } from '../hooks/usePageMeta';
import { PageLoading, PageError, PageEmpty } from '../components/PageStatus';
import { truncateText } from '../utils/text';
import { formatArticleCategories } from '../utils/articleCategories';
import { formatLocalDateTime } from '../utils/formatDate';
import './EntityPage.css';

const SORT_OPTIONS = [
  { id: 'recent', label: 'Recent', hint: 'Newest published first' },
  { id: 'prominence', label: 'Prominence', hint: 'Strongest entity mentions first' },
  { id: 'indexed', label: 'Indexed', hint: 'Newest graph links first' },
  { id: 'source', label: 'Source', hint: 'Outlet name A–Z, then date' },
];

function resolveDays(searchParams) {
  if (searchParams.has('days')) {
    const parsed = Number(searchParams.get('days'));
    return parsed >= 1 ? parsed : 7;
  }
  return 7;
}

function resolveSort(searchParams) {
  const raw = searchParams.get('sort');
  return SORT_OPTIONS.some((option) => option.id === raw) ? raw : 'recent';
}

function EntityPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const days = resolveDays(searchParams);
  const sort = resolveSort(searchParams);
  const window = useMemo(() => windowRange(days), [days]);

  const [entity, setEntity] = useState(null);
  const [articles, setArticles] = useState([]);
  const [relatedEntities, setRelatedEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeSort = SORT_OPTIONS.find((option) => option.id === sort) || SORT_OPTIONS[0];

  const pageDescription = useMemo(() => {
    if (!entity) return undefined;
    return truncateText(
      `${entity.label} — ${entity.articleCount} ${pluralize(entity.articleCount, 'article')} in the story graph over ${windowLabel(days)}.`,
    );
  }, [entity, days]);

  usePageMeta({
    title: entity?.label ? `${entity.label} · Entity timeline` : 'Entity',
    description: pageDescription,
    path: slug ? `/entity/${slug}` : undefined,
  });

  const loadEntity = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const entityData = await fetchEntityBySlug(slug, window);
      setEntity(entityData);
      const [articleData, neighborData] = await Promise.all([
        fetchEntityArticles(entityData.id, { ...window, size: 50, sort }),
        fetchRelatedEntities(entityData.id, { ...window, limit: 8 }),
      ]);
      setArticles(articleData);
      setRelatedEntities(neighborData);
    } catch (err) {
      setEntity(null);
      setArticles([]);
      setRelatedEntities([]);
      setError(err.status === 404 ? 'Entity not found' : err.message);
    } finally {
      setLoading(false);
    }
  }, [slug, window, sort]);

  useEffect(() => {
    loadEntity();
  }, [loadEntity]);

  const updateParams = (patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, value]) => {
        next.set(key, String(value));
      });
      return next;
    });
  };

  const graphHref = entity
    ? `/web?q=${encodeURIComponent(entity.slug || entity.label)}&e=${entity.id}&days=${days}`
    : '/web';

  return (
    <div className="reader-page entity-page">
      <div className="container">
        <header className="reader-header reader-header--split entity-header">
          <div className="reader-header-main">
            <p className="reader-kicker">Story graph</p>
            <h1>{entity?.label || slug?.replace(/-/g, ' ') || 'Entity'}</h1>
            {entity && (
              <p className="reader-lead">
                {entity.type}
                {' · '}
                {entity.articleCount} {pluralize(entity.articleCount, 'article')} in {windowLabel(days)}
                {entity.aliases?.length > 0 && (
                  <>
                    {' · '}
                    Also known as {entity.aliases.slice(0, 3).join(', ')}
                  </>
                )}
              </p>
            )}
            {!entity && !loading && !error && (
              <p className="reader-lead">Timeline of articles mentioning this entity.</p>
            )}
          </div>
          <div className="entity-header-actions">
            <Link to={graphHref} className="reader-btn entity-web-cta">
              View in Memetic Web
            </Link>
            <Link to="/web" className="reader-btn-secondary">
              Open graph explorer
            </Link>
          </div>
        </header>

        {!loading && !error && relatedEntities.length > 0 && (
          <section className="entity-related" aria-label="Related entities">
            <h2 className="entity-related-title">Connected entities</h2>
            <ul className="entity-related-list">
              {relatedEntities.map((neighbor) => (
                <li key={neighbor.id}>
                  {neighbor.slug ? (
                    <Link to={`/entity/${neighbor.slug}`} className="entity-related-chip">
                      <span>{neighbor.label}</span>
                      <span className="entity-related-type">{neighbor.type}</span>
                    </Link>
                  ) : (
                    <span className="entity-related-chip entity-related-chip--static">
                      <span>{neighbor.label}</span>
                      <span className="entity-related-type">{neighbor.type}</span>
                    </span>
                  )}
                  <Link
                    to={`/web?q=${encodeURIComponent(neighbor.slug || neighbor.label)}&e=${neighbor.id}&days=${days}`}
                    className="entity-related-graph-link"
                  >
                    Graph
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="entity-controls">
          <div className="entity-control-group" role="group" aria-label="Time window">
            <span className="entity-control-label">Window</span>
            <div className="entity-window-pills">
              {WINDOW_OPTIONS.map((option) => (
                <button
                  key={option.days}
                  type="button"
                  className={`entity-window-pill${days === option.days ? ' entity-window-pill--active' : ''}`}
                  aria-pressed={days === option.days}
                  onClick={() => updateParams({ days: option.days })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="entity-control-group" role="group" aria-label="Article sort order">
            <span className="entity-control-label">Sort</span>
            <div className="entity-window-pills">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`entity-window-pill${sort === option.id ? ' entity-window-pill--active' : ''}`}
                  aria-pressed={sort === option.id}
                  title={option.hint}
                  onClick={() => updateParams({ sort: option.id })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!loading && !error && articles.length > 0 && (
          <p className="entity-sort-hint" role="status">
            Sorted by <strong>{activeSort.label.toLowerCase()}</strong> — {activeSort.hint.toLowerCase()}.
          </p>
        )}

        {loading && <PageLoading message="Loading entity timeline…" />}
        {error && (
          <PageError title="Could not load entity" message={error} onRetry={loadEntity}>
            <Link to="/web" className="reader-btn-secondary mt-2">
              Back to graph explorer
            </Link>
          </PageError>
        )}

        {!loading && !error && entity && articles.length === 0 && (
          <PageEmpty
            title="No articles in this window"
            message={`No articles mention ${entity.label} in ${windowLabel(days)}.`}
          >
            <Link to={graphHref} className="reader-btn-secondary mt-2">
              Explore in Memetic Web
            </Link>
          </PageEmpty>
        )}

        {!loading && !error && articles.length > 0 && (
          <section aria-label={`Articles mentioning ${entity?.label || slug}`}>
            <h2 className="visually-hidden">Article timeline</h2>
            <ol className="entity-timeline">
              {articles.map((article) => (
                <li key={article.id} className="entity-timeline-row">
                  <time
                    className="entity-timeline-time"
                    dateTime={article.publishedAt || undefined}
                  >
                    {article.publishedAt
                      ? formatLocalDateTime(article.publishedAt)
                      : 'Unknown date'}
                  </time>
                  <div className="entity-timeline-body">
                    <Link to={`/article/${article.id}`} className="entity-timeline-title">
                      {article.title}
                    </Link>
                    <p className="entity-timeline-meta">
                      {[article.source || 'Unknown source', formatArticleCategories(article)]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}

export default EntityPage;
